// Translucent HUD pieces floating over the 3D table. Only what matters during play is
// always shown; details fold out on demand.
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState, type ReactNode } from "react";

import * as uf from "../../../../common/unfathomable";
import { art } from "./assets";
import { CardFace, CardTip, useTooltip } from "./CardFace";
import { kSeatColors } from "./seatColors";
import { Rich } from "./RichText";
import css from "./Unfathomable.module.css";

const kResIcon: Record<uf.Resource, string> = { fuel: "⛽", food: "🥫", sanity: "🧠", souls: "👥" };

function Gauge({ r, v }: { r: uf.Resource; v: number }) {
  const pct = Math.max(0, Math.min(1, v / 10));
  const danger = v <= 2;
  // Bump a key on every loss so the CSS animation replays.
  const prev = useRef(v);
  const [hit, setHit] = useState(0);
  useEffect(() => {
    if (v < prev.current) setHit(h => h + 1);
    prev.current = v;
  }, [v]);
  return (
    <div key={hit} className={`${css.gauge} ${hit ? css.gaugeHit : ""}`} data-danger={danger ? "1" : "0"} title={`${uf.kResourceNameZh[r]} ${v}`}>
      <svg viewBox="-22 -22 44 44" width={44} height={44}>
        <circle r={18} fill="rgba(7,20,22,0.7)" stroke="rgba(184,146,90,0.5)" strokeWidth={2} />
        <circle r={18} fill="none" stroke={danger ? "#ff6a5a" : "#ecd29a"} strokeWidth={3.5} strokeDasharray={`${pct * 113} 113`}
          transform="rotate(-90)" strokeLinecap="round" style={{ transition: "stroke-dasharray 0.8s" }} />
        <text textAnchor="middle" dy={5} fontSize={14} fill={danger ? "#ff8b7d" : "#f0e3c2"} fontWeight={700}>{v}</text>
      </svg>
      <div className={css.gaugeLabel}>{kResIcon[r]} {uf.kResourceNameZh[r]}</div>
    </div>
  );
}

function Track({ label, value, color, end }: { label: string; value: number; color: string; end: string }) {
  return (
    <div className={css.track}>
      <span className={css.trackLabel}>{label}</span>
      {[0, 1, 2, 3, 4].map(i => (
        <span key={i} className={css.trackPip} style={i <= value ? { background: color, boxShadow: `0 0 8px ${color}` } : undefined}>
          {i === 4 ? end : i === 0 ? "起" : ""}
        </span>
      ))}
    </div>
  );
}

/** The voyage so far: one segment per waypoint played (width = its distance), up to 12. */
function VoyageBar({ view, artOk }: { view: uf.GameView; artOk: boolean }) {
  const total = view.distance;
  const scale = Math.max(12, total);
  return (
    <div className={css.voyage}>
      <div className={css.voyageHead}>
        <span>航程</span><b>{total}</b><span>/ 12</span>
        {total >= 12 && <span className={css.voyageReady}>抵达即胜利</span>}
      </div>
      <div className={css.voyageTrack}>
        {view.waypointsInPlay.map((w, i) => <VoyageLeg key={`${w}-${i}`} id={w} scale={scale} />)}
        <div className={css.voyageGoal} style={{ left: `${(12 / scale) * 100}%` }} title="航程 12：抵达时人类获胜" />
      </div>
      {view.waypointsInPlay.length === 0 ? <div className={css.voyageEmpty}>尚未驶过任何航点</div> : (
        <div className={css.voyageList}>
          {view.waypointsInPlay.map((w, i) => <VoyageCard key={`${w}-${i}`} id={w} by={view.players[view.waypointChoosers[i]]?.name ?? "?"} artOk={artOk} />)}
        </div>
      )}
    </div>
  );
}

/** One waypoint played: thumbnail, name, distance and who chose it. */
function VoyageCard({ id, by, artOk }: { id: string; by: string; artOk: boolean }) {
  const w = uf.kWaypointById[id];
  return (
    <div className={css.voyageCard}>
      <CardFace kind="waypoint" id={id} artOk={artOk} width={26} />
      <div className={css.voyageCardText}>
        <div className={css.voyageCardName}>{w.name}</div>
        <div className={css.voyageCardBy}>⚓ {by} 选择</div>
      </div>
      <div className={css.voyageCardDist}>{w.distance}</div>
    </div>
  );
}

function VoyageLeg({ id, scale }: { id: string; scale: number }) {
  const w = uf.kWaypointById[id];
  const tip = useTooltip(<CardTip kind="waypoint" id={id} />);
  return (
    <div className={css.voyageLeg} style={{ width: `calc(${(w.distance / scale) * 100}% - 2px)`, flex: "none" }} {...tip}>
      <span>{w.name}</span><i>{w.distance}</i>
    </div>
  );
}

const kTitleInfo = {
  captain: { icon: "⚓", label: "船长", tip: "船长：航迹走满、驶向新航点时，由船长在两张航点卡中选定航向；部分神话卡也要船长做决定" },
  keeper: { icon: "📖", label: "典籍守护者", tip: "典籍守护者：每回合可用一次行动，查看法术牌堆顶 2 张并结算其中 1 张" },
} as const;

/** Who holds the two titles — big enough to read at a glance; pops when a title changes hands. */
function TitleHolders({ view, artOk }: { view: uf.GameView; artOk: boolean }) {
  return (
    <div className={css.titleHolders}>
      {(["captain", "keeper"] as const).map(t => {
        const seat = view.titles[t];
        const p = seat === null ? null : view.players[seat];
        const info = kTitleInfo[t];
        return (
          <motion.div key={`${t}-${seat}`} className={css.titleHolder} data-title={t} title={info.tip}
            initial={{ scale: 1.25, boxShadow: "0 0 22px rgba(255, 224, 138, 0.9)" }} animate={{ scale: 1, boxShadow: "0 0 0px rgba(255, 224, 138, 0)" }} transition={{ duration: 0.9 }}>
            <div className={css.titleHolderLabel}>{info.icon} {info.label}</div>
            {p ? (
              <div className={css.titleHolderWho}>
                {artOk ? <img className={css.titleHolderPortrait} style={{ borderColor: kSeatColors[p.seat % kSeatColors.length] }} src={art.token(p.character)} alt="" />
                  : <span className={css.titleHolderPortrait} style={{ borderColor: kSeatColors[p.seat % kSeatColors.length] }}>{uf.kCharacters[p.character].name[0]}</span>}
                <span className={css.titleHolderName}>{p.name}{p.seat === view.you ? "（你）" : ""}</span>
              </div>
            ) : <div className={css.titleHolderWho} style={{ opacity: 0.6 }}>空缺</div>}
          </motion.div>
        );
      })}
    </div>
  );
}

export function StatusHud({ view, artOk }: { view: uf.GameView; artOk: boolean }) {
  const [open, setOpen] = useState(false);
  const d = view.decks;
  return (
    <div className={`${css.hudPanel} ${css.statusHud}`}>
      <div className={css.gauges}>
        {(Object.keys(view.resources) as uf.Resource[]).map(r => <Gauge key={r} r={r} v={view.resources[r]} />)}
      </div>
      <TitleHolders view={view} artOk={artOk} />
      <Track label="航迹" value={view.travel} color="#7fd6c8" end="达" />
      <Track label="仪式" value={view.ritual} color="#c38cff" end="咒" />
      <VoyageBar view={view} artOk={artOk} />
      <div className={css.hudRow}>
        {view.awakeningDone && <span className={css.badgeRed} style={{ padding: "0 6px", borderRadius: 8 }}>已觉醒</span>}
        <button className={css.hudLink} onClick={() => setOpen(o => !o)}>{open ? "收起 ▴" : "详情 ▾"}</button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div className={css.hudDetails} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
            <div>供应区：深潜者 {view.deepOneSupply} · 乘客 {view.passengerSupply}</div>
            <div>神话 {d.mythos} · 航点 {d.waypoint} · 物品 {d.item} · 法术 {d.spell} · 损伤 {d.damage} · 混沌 {d.chaos}</div>
            <div>{(Object.keys(d.skill) as uf.SkillType[]).map(t => `${uf.kSkillNameZh[t]} ${d.skill[t]}`).join(" · ")}</div>
            <div>已走航点：{view.waypointsInPlay.map(w => `${uf.kWaypointById[w].name}(${uf.kWaypointById[w].distance})`).join("、") || "无"}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------- players
/** Shown next to the room host (the first seated human who is online; see server/unfathomable/session.ts). */
export const kHostTip = "房主：座位最靠前、当前在线的真人玩家；房主掉线时自动交给下一位";

function PlayerChip({ p, view, artOk, host }: { p: uf.PlayerView; view: uf.GameView; artOk: boolean; host: boolean }) {
  const [open, setOpen] = useState(false);
  const ch = uf.kCharacters[p.character];
  const color = p.revealed ? "#c94040" : kSeatColors[p.seat % kSeatColors.length];
  const tip = useTooltip(
    <div>
      <b>{ch.name}（{ch.title}）</b>
      <div style={{ marginTop: 4 }}><b>{ch.ability.name}</b>：<Rich text={ch.ability.text} /></div>
      <div style={{ marginTop: 4 }}><b>叛变能力（公开身份后可用）：{ch.reveal.name}</b>：<Rich text={ch.reveal.text} /></div>
      <div style={{ marginTop: 4 }}><b>特技：{ch.feat.name}</b>{p.featAvailable ? "" : "（已使用）"}：<Rich text={ch.feat.text} /></div>
    </div>,
  );
  return (
    <div className={`${css.playerChip} ${p.seat === view.current ? css.playerChipCurrent : ""} ${p.connected === false ? css.offline : ""}`}>
      <div className={css.playerChipMain} onClick={() => setOpen(o => !o)} {...tip}>
        {artOk ? <img className={css.chipPortrait} style={{ borderColor: color }} src={art.token(p.character)} alt="" />
          : <div className={css.chipPortrait} style={{ borderColor: color }}>{ch.name[0]}</div>}
        <div style={{ minWidth: 0 }}>
          <div className={css.chipName}>
            {p.seat === view.firstPlayer && <span title="起始玩家">① </span>}{p.name}{p.seat === view.you ? "（你）" : ""}
            {host && <span title={kHostTip}> 👑</span>}
          </div>
          {(view.titles.captain === p.seat || view.titles.keeper === p.seat) && (
            <div className={css.chipTitles}>
              {(["captain", "keeper"] as const).filter(t => view.titles[t] === p.seat).map(t => (
                <motion.span key={t} className={css.chipTitle} data-title={t} title={kTitleInfo[t].tip}
                  initial={{ scale: 1.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 14 }}>
                  {kTitleInfo[t].icon} {kTitleInfo[t].label}
                </motion.span>
              ))}
            </div>
          )}
          <div className={css.chipSub}>{uf.spaceName(p.space)} · 🂠{p.handCount}
            {p.revealed && <span style={{ color: "#ff9d92" }}> · {p.revealed === "hybrid" ? "混血叛徒" : "邪教徒"}</span>}
          </div>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div className={css.chipDetails} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
            <div>{ch.name}（{ch.title}）· 忠诚卡 {p.loyaltyCount}</div>
            <div className={css.chipBadges}>
              {p.items.map(it => <ItemBadge key={it.def} def={it.def} inactive={!!uf.kItemById[it.def].improvement && !it.faceUp} />)}
              {p.playArea.map((c, i) => <PlayAreaBadge key={i} c={c} />)}
              {host && <span className={css.badge} title={kHostTip}>👑 房主</span>}
              {p.featAvailable && <span className={css.badge}>特技可用</span>}
              {p.connected === false && <span className={css.badge}>离线</span>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ItemBadge({ def, inactive }: { def: string; inactive: boolean }) {
  const tip = useTooltip(<CardTip kind="item" id={def} />);
  return <span className={css.badge} style={{ opacity: inactive ? 0.5 : 1 }} {...tip}>{uf.kItemById[def].name}</span>;
}

function PlayAreaBadge({ c }: { c: uf.PlayAreaCard }) {
  const tip = useTooltip(<CardTip kind={c.kind === "spell" ? "spell" : "mythos"} id={c.def} />);
  const name = c.kind === "spell" ? uf.kSpellById[c.def].name : uf.kMythosById[c.def].name;
  return <span className={`${css.badge} ${css.badgeRed}`} {...tip}>{name}</span>;
}

/** Players in turn order from the first player. Click a player for details. */
export function PlayersHud({ view, artOk, hostSeat }: { view: uf.GameView; artOk: boolean; hostSeat: number | null }) {
  const order = view.players.map((_, i) => view.players[(view.firstPlayer + i) % view.players.length]);
  return (
    <div className={css.playersHud}>
      {order.map(p => <PlayerChip key={p.seat} p={p} view={view} artOk={artOk} host={p.seat === hostSeat} />)}
    </div>
  );
}

// ---------------------------------------------------------------- menu
export function HudMenu({ children, label = "☰ 菜单" }: { children: ReactNode; label?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={css.hudMenu}>
      <button className={`${css.btn} ${css.btnSmall}`} onClick={() => setOpen(o => !o)}>{label}</button>
      <AnimatePresence>
        {open && (
          <motion.div className={`${css.hudMenuList} ${css.darkFrame}`} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            onClick={() => setOpen(false)}>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * A menu button that asks again before acting: the first click turns it into "确定？", the second
 * acts. (Not window.confirm: embedded browsers may block dialogs and return false silently.)
 */
export function ConfirmButton({ className, label, confirmLabel, onConfirm }: {
  className?: string; label: ReactNode; confirmLabel: ReactNode; onConfirm: () => void;
}) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button className={className} onClick={e => {
      if (armed) { setArmed(false); onConfirm(); return; }
      e.stopPropagation(); // keep the menu open for the second click
      setArmed(true);
    }}>{armed ? confirmLabel : label}</button>
  );
}
